import {
  apiTambahInboisDetail,
  apiTambahInboisDetailSingle,
  apiUpdateInboisDetail,
  apiGetInboisDetailByNoInbois,
} from "api/inbois_detail";
import { apiUpdateInbois, apiTambahInbois } from "api/inbois";
import { apiTambahLejar, apiUpdateLejarByNoInbois } from "api/lejar";
import { apiGetAkaunByNoAkaun } from "api/akaun";
import React, { useState, useEffect } from "react";
import { useSnackbar } from "components/GlobalSnackbar";
import { handleApiError } from "utils/errorHandler";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
} from "@mui/material";
import { buttonStyles } from "utils/buttonStyles";
import { dialogHeaderStyles, dialogContentStyles } from "utils/dialogStyles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import MDTypography from "components/MDTypography";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Delete";
import { apiGetMaxNoInbois } from "api/inbois";
import { autoCapitalizePayload } from "utils/autoCapitalize";
// For edit mode (autofillData)
const defaultHeaderFields = {
  tarikh_inbois: "",
  no_inbois: "",
  // ...other header fields
};

function TambahInboisPelbagai({ open, onClose, onSubmit, onSuccess, selectedAkaun, autofillData }) {
  const { showSuccess, showError, showWarning } = useSnackbar();
  const [headerFields, setHeaderFields] = useState(defaultHeaderFields);
  const [detailRows, setDetailRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [draftInboisId, setDraftInboisId] = useState(null); // For ADD mode: stores the parent inbois ID

  // Helper: get current user from localStorage
  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  })();

  // State for generated no_inbois (for add mode)
  const [newNoInbois, setNewNoInbois] = useState("");
  // Table data for add mode
  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  // Autofill for edit mode
  useEffect(() => {
    if (open && autofillData && autofillData.header) {
      setHeaderFields({ ...autofillData.header });
      // Map DB id to detailId for edit mode
      setDetailRows(
        Array.isArray(autofillData.details)
          ? autofillData.details.map((row) => ({
              ...row,
              detailId: row.id, // ensure DB id is available for update
              editable: false,
            }))
          : []
      );
    } else if (open && !autofillData) {
      setHeaderFields(defaultHeaderFields);
      setDetailRows([]);
    }
  }, [open, autofillData]);
  // Reset popup state when opened (add mode only)
  useEffect(() => {
    if (open && !autofillData) {
      setError("");
      setSaving(false);
      setRows([]);
      setSelectedIds([]);
      setNewNoInbois("");
      setHeaderFields(defaultHeaderFields);
      setDetailRows([]);
      
      // Try to restore draft ID from localStorage if recent (within 5 minutes)
      const storedDraftId = localStorage.getItem("draftInboisId");
      const storedTimestamp = localStorage.getItem("draftInboisTimestamp");
      const now = Date.now();
      if (storedDraftId && storedTimestamp && (now - Number(storedTimestamp)) < 300000) {
        setDraftInboisId(Number(storedDraftId));
      } else {
        setDraftInboisId(null);
        localStorage.removeItem("draftInboisId");
        localStorage.removeItem("draftInboisTimestamp");
      }
      
      // Optionally reset tarikhInbois to today
      setTarikhInbois(() => {
        const today = new Date();
        return today.toISOString().slice(0, 10);
      });
      (async () => {
        try {
          const now = Date.now();
          
          // Check if we have a recent draft
          const storedDraftId = localStorage.getItem("draftInboisId");
          const storedDraftTimestamp = localStorage.getItem("draftInboisTimestamp");
          const storedNoInbois = localStorage.getItem("pendingNoInbois");
          
          if (storedDraftId && storedDraftTimestamp && storedNoInbois && 
              (now - Number(storedDraftTimestamp)) < 300000) {
            // Reuse existing draft
            setDraftInboisId(Number(storedDraftId));
            setNewNoInbois(storedNoInbois);
          } else {
            // Fetch max, calculate +1, but DON'T create draft yet
            // Draft will be created when user clicks SIMPAN
            const maxNoInbois = await apiGetMaxNoInbois("Open Inbois (Pelbagai)");
            console.log("Max No Inbois from API:", maxNoInbois);
            
            let nextNoInbois;
            if (maxNoInbois && maxNoInbois.match(/^8(\d+)-PBG$/)) {
              // Extract numeric part from 8XXXXXXXX-PBG format
              const match = maxNoInbois.match(/^8(\d+)-PBG$/);
              const numericPart = parseInt(match[1], 10);
              const nextNumeric = numericPart + 1;
              nextNoInbois = `8${String(nextNumeric).padStart(8, '0')}-PBG`;
              console.log(`Max: ${maxNoInbois}, Numeric: ${numericPart}, Next: ${nextNoInbois}`);
            } else {
              // First invoice for "Open Inbois (Pelbagai)" - start from 800000001-PBG
              nextNoInbois = "800000001-PBG";
              console.log("No existing invoice found, starting with:", nextNoInbois);
            }
            
            setNewNoInbois(nextNoInbois);
            
            // Clear old draft data from localStorage
            localStorage.removeItem("draftInboisId");
            localStorage.removeItem("draftInboisTimestamp");
            localStorage.removeItem("pendingNoInbois");
            localStorage.removeItem("pendingNoInboisTimestamp");
          }
        } catch (err) {
          console.error("Failed to initialize draft inbois:", err);
          setNewNoInbois("");
        }
      })();
    }
  }, [open, autofillData]);

  // Create draft inbois immediately when popup opens (ADD mode)
  const createDraftInboisOnOpen = async (noInbois) => {
    const noAkaunRef = selectedAkaun?.noakaun || selectedAkaun?.no_akaun || "";
    if (!noAkaunRef) {
      throw new Error("No Akaun tidak tersedia");
    }

    // Get akaun details
    const res = await apiGetAkaunByNoAkaun(noAkaunRef);
    const akaunDetails = Array.isArray(res?.data) ? res.data[0] : res?.data;
    if (!akaunDetails) {
      throw new Error("Maklumat akaun tidak dijumpai");
    }

    const user = (() => {
      try {
        return JSON.parse(localStorage.getItem("user") || "{}");
      } catch {
        return {};
      }
    })();

    const now = new Date();
    const isoTarikh = tarikhInbois ? new Date(tarikhInbois).toISOString() : now.toISOString();
    const nowIso = now.toISOString();

    // Create draft inbois record
    const payload = {
      mark_batal: 0,
      mark_delete: 0,
      tarikh_inbois: isoTarikh,
      tarikh_simpan: nowIso,
      tarikh_bayar: null,
      tarikh_terakhir_jana: "",
      jumlah: 0, // Will be updated when user clicks SIMPAN
      jumlah_total_detail: 0,
      perkara: "Open Inbois (Pelbagai)",
      mark_post: 0,
      bahagian_pemantau: user.bahagian || "",
      id_pengguna: (user.id_pengguna || "").toLowerCase(),
      id_inbois_bahagian: 0,
      id_akaun: 0,
      no_akaun: noAkaunRef,
      no_inbois: noInbois,
      kod_harta: akaunDetails.kodharta || akaunDetails.kod_harta || "",
      bil_hari_lewat_bayar: 0,
      notis_pertama: 0,
      status_notis_pertama: 0,
      notis_kedua: 0,
      status_notis_kedua: 0,
      notis_ketiga: 0,
      status_notis_ketiga: 0,
      the_diff: 0,
      aging_till6: 0,
      aging7_12: 0,
      aging13_18: 0,
      aging19_24: 0,
      aging25_30: 0,
      aging31_36: 0,
      aging37_72: 0,
      aging73_120: 0,
      aging_more120: 0,
      no_resit: "",
      kategori_inbois: "Open",
      tunggakan_to_date: 0.0,
      denda_to_date: 0.0,
      baki_akhir_lejar: 0.0,
      peratus_diskaun: 0.0,
      mark_bayar: 0,
      jumlah_bayar: 0.0,
    };

    const inboisRes = await apiTambahInbois(payload);
    const inboisId = inboisRes && inboisRes.id ? inboisRes.id : null;
    if (!inboisId) {
      throw new Error("Gagal mendapatkan ID inbois yang baru disimpan");
    }

    setDraftInboisId(inboisId);
    // Store in localStorage for persistence
    localStorage.setItem("draftInboisId", String(inboisId));
    localStorage.setItem("draftInboisTimestamp", String(Date.now()));
    localStorage.setItem("pendingNoInbois", noInbois);
    localStorage.setItem("pendingNoInboisTimestamp", String(Date.now()));
    
    return inboisId;
  };

  // Create draft inbois record for ADD mode (called when first detail row is saved in EDIT mode)
  const createDraftInbois = async () => {
    if (draftInboisId) return draftInboisId; // Already created
    
    const noAkaunRef = selectedAkaun?.noakaun || selectedAkaun?.no_akaun || "";
    if (!noAkaunRef || !newNoInbois) {
      throw new Error("No Akaun atau No Inbois tidak tersedia");
    }

    // Get akaun details
    const res = await apiGetAkaunByNoAkaun(noAkaunRef);
    const akaunDetails = Array.isArray(res?.data) ? res.data[0] : res?.data;
    if (!akaunDetails) {
      throw new Error("Maklumat akaun tidak dijumpai");
    }

    const user = (() => {
      try {
        return JSON.parse(localStorage.getItem("user") || "{}");
      } catch {
        return {};
      }
    })();

    const isoTarikh = tarikhInbois ? new Date(tarikhInbois).toISOString() : new Date().toISOString();
    const nowIso = new Date().toISOString();

    // Create draft inbois record
    const payload = {
      mark_batal: 0,
      mark_delete: 0,
      tarikh_inbois: isoTarikh,
      tarikh_simpan: nowIso,
      tarikh_bayar: null,
      tarikh_terakhir_jana: "",
      jumlah: 0, // Will be updated when user clicks SIMPAN
      jumlah_total_detail: 0,
      perkara: "Open Inbois (Pelbagai)",
      mark_post: 0,
      bahagian_pemantau: user.bahagian || "",
      id_pengguna: (user.id_pengguna || "").toLowerCase(),
      id_inbois_bahagian: 0,
      id_akaun: 0,
      no_akaun: noAkaunRef,
      no_inbois: newNoInbois,
      kod_harta: akaunDetails.kodharta || akaunDetails.kod_harta || "",
      bil_hari_lewat_bayar: 0,
      notis_pertama: 0,
      status_notis_pertama: 0,
      notis_kedua: 0,
      status_notis_kedua: 0,
      notis_ketiga: 0,
      status_notis_ketiga: 0,
      the_diff: 0,
      aging_till6: 0,
      aging7_12: 0,
      aging13_18: 0,
      aging19_24: 0,
      aging25_30: 0,
      aging31_36: 0,
      aging37_72: 0,
      aging73_120: 0,
      aging_more120: 0,
      no_resit: "",
      kategori_inbois: "Open",
      tunggakan_to_date: 0.0,
      denda_to_date: 0.0,
      baki_akhir_lejar: 0.0,
      peratus_diskaun: 0.0,
      mark_bayar: 0,
      jumlah_bayar: 0.0,
    };

    const inboisRes = await apiTambahInbois(payload);
    const inboisId = inboisRes && inboisRes.id ? inboisRes.id : null;
    if (!inboisId) {
      throw new Error("Gagal mendapatkan ID inbois yang baru disimpan");
    }

    setDraftInboisId(inboisId);
    // Store in localStorage for persistence
    localStorage.setItem("draftInboisId", String(inboisId));
    localStorage.setItem("draftInboisTimestamp", String(Date.now()));
    return inboisId;
  };

  const handleSimpan = async () => {
    setError("");
    setSaving(true);
    const noAkaunRef = selectedAkaun?.noakaun || selectedAkaun?.no_akaun || "";
    // Apply capitalization to header fields (non-numeric/string only)
    const capHeaderFields = autoCapitalizePayload(headerFields, ["no_inbois"]);
    let akaunDetails;
    try {
      const res = await apiGetAkaunByNoAkaun(noAkaunRef);
      akaunDetails = Array.isArray(res?.data) ? res.data[0] : res?.data;
    } catch (err) {
      showError("Gagal mendapatkan maklumat akaun untuk Kadar Sewa dan Kategori Kutipan.");
      setSaving(false);
      return;
    }
    if (!akaunDetails || !akaunDetails.kadar_sewa || !akaunDetails.kategori_kutipan) {
      showError("Maklumat Kadar Sewa atau Kategori Kutipan tidak lengkap pada akaun ini.");
      setSaving(false);
      return;
    }
    if (!noAkaunRef) {
      showError("Rujukan No Akaun tiada. Sila pilih akaun yang sah.");
      setSaving(false);
      return;
    }
    if (rows.length === 0) {
      showWarning("Tiada item untuk disimpan.");
      setSaving(false);
      return;
    }
    const hasEditable = rows.some((row) => row.editable);
    if (hasEditable) {
      showWarning(
        "Sila sahkan semua item sebelum simpan. Terdapat item yang masih belum disahkan."
      );
      setSaving(false);
      return;
    }
    const totalJumlah = rows.reduce((sum, row) => sum + Number(row.jumlah || 0), 0);
    // Use generated newNoInbois
    if (!newNoInbois) {
      showError("No Inbois tidak dapat dijana. Sila cuba lagi.");
      setSaving(false);
      return;
    }

    // Convert tarikhInbois to ISO string for all lejar date fields
    const isoTarikh = new Date(tarikhInbois).toISOString();
    const nowIso = new Date().toISOString();

    const payload = {
      mark_batal: 0,
      mark_delete: 0,
      tarikh_inbois: isoTarikh,
      tarikh_simpan: nowIso,
      tarikh_bayar: null, // set to null for Prisma DateTime compatibility
      tarikh_terakhir_jana: "", // akan dikemaskini di tempat lain
      jumlah: parseFloat(totalJumlah),
      jumlah_total_detail: 0.0,
      perkara: "Open Inbois (Pelbagai)",
      mark_post: 0,
      bahagian_pemantau: user.bahagian || "",
      id_pengguna: (user.id_pengguna || "").toLowerCase(),
      id_inbois_bahagian: 0,
      id_akaun: 0,
      no_akaun: noAkaunRef,
      no_inbois: newNoInbois,
      kod_harta: akaunDetails.kodharta || akaunDetails.kod_harta || "",
      bil_hari_lewat_bayar: 0,
      notis_pertama: 0,
      status_notis_pertama: 0,
      notis_kedua: 0,
      status_notis_kedua: 0,
      notis_ketiga: 0,
      status_notis_ketiga: 0,
      the_diff: 0,
      aging_till6: 0,
      aging7_12: 0,
      aging13_18: 0,
      aging19_24: 0,
      aging25_30: 0,
      aging31_36: 0,
      aging37_72: 0,
      aging73_120: 0,
      aging_more120: 0,
      no_resit: "",
      kategori_inbois: "Open",
      tunggakan_to_date: 0.0,
      denda_to_date: 0.0,
      baki_akhir_lejar: 0.0,
      peratus_diskaun: 0.0,
      mark_bayar: 0,
      jumlah_bayar: 0.0,
    };
    try {
      // Simpan inbois dan dapatkan rekod yang dimasukkan (dengan id)
      const inboisRes = await apiTambahInbois(payload);
      const inboisId = inboisRes && inboisRes.id ? inboisRes.id : null;
      if (!inboisId) {
        showError("Gagal mendapatkan ID inbois yang baru disimpan.");
        setSaving(false);
        return;
      }
      
      // Save all detail rows to inbois_detail table
      console.log("Saving detail rows to inbois_detail with inboisId:", inboisId);
      for (const row of rows) {
        try {
          await apiTambahInboisDetailSingle({
            id_inbois: inboisId,
            butiran: row.butiran || "",
            kuantiti: parseFloat(row.kuantiti || 0),
            kadar: parseFloat(row.kadar || 0),
            jumlah: parseFloat(row.jumlah || 0),
            bil: "",
          });
        } catch (detailErr) {
          console.error("Failed to save detail row:", row, detailErr);
          showError(`Gagal menyimpan item: ${row.butiran}`);
          setSaving(false);
          return;
        }
      }
      
      // After saving all details, sum jumlah and update inbois.jumlah
      let sumJumlah = 0;
      try {
        // Fetch all details for this no_inbois to verify and sum
        const detailRes = await apiGetInboisDetailByNoInbois(newNoInbois);
        sumJumlah = (detailRes.data || []).reduce((sum, row) => sum + Number(row.jumlah || 0), 0);
        // Update inbois.jumlah in DB
        await apiUpdateInbois(inboisId, { jumlah: sumJumlah });
      } catch (e) {
        // If update fails, just continue
        console.error("Failed to update jumlah in inbois:", e);
      }
      // --- DO NOT create lejar entry on save ---
      // Lejar entry will only be created when Post button is clicked (mark_post: 0 -> 1)
      // This prevents "Open Inbois (Pelbagai)" from appearing in lejar before posting
      // --- End: No lejar creation on save ---
    } catch (e) {
      handleApiError(e, showError);
      setSaving(false);
      return;
    }
    // Clear localStorage after successful save
    localStorage.removeItem("pendingNoInbois");
    localStorage.removeItem("pendingNoInboisTimestamp");
    localStorage.removeItem("draftInboisId");
    localStorage.removeItem("draftInboisTimestamp");
    
    showSuccess(`Inbois Pelbagai berjaya disimpan! No Inbois: ${newNoInbois}`);
    
    if (onSuccess) onSuccess(payload);
    if (onSubmit) onSubmit(payload);
    onClose();
    setSaving(false);
  };
  const [tarikhInbois, setTarikhInbois] = useState(() => {
    const today = new Date();
    return today.toISOString().slice(0, 10);
  });

  // Add new item
  const handleAddItem = () => {
    const newId = Date.now();
    if (autofillData) {
      setDetailRows((prev) => [
        ...prev,
        {
          id: newId,
          butiran: "",
          kuantiti: 1,
          kadar: 0,
          jumlah: 0,
          checked: false,
          editable: true,
        },
      ]);
      // Handler for Post button: (disabled, handled in JanaInbois.js)
      // Posting to lejar is now only allowed from the main Post button in JanaInbois.js
    } else {
      setRows([
        ...rows,
        {
          id: newId,
          butiran: "",
          kuantiti: 1,
          kadar: 0,
          jumlah: 0,
          checked: false,
          editable: true,
        },
      ]);
    }
  };
  // Edit handler
  const handleEditItem = (id) => {
    if (autofillData) {
      setDetailRows((rows) =>
        rows.map((row) => (row.id === id ? { ...row, editable: true } : row))
      );
    } else {
      setRows(rows.map((row) => (row.id === id ? { ...row, editable: true } : row)));
    }
  };

  const handleDeleteItem = (id) => {
    if (autofillData) {
      setDetailRows((rows) => rows.filter((row) => row.id !== id));
    } else {
      setRows(rows.filter((row) => row.id !== id));
      setSelectedIds(selectedIds.filter((x) => x !== id));
    }
  };

  // Save or update detail row
  const handleSaveRow = async (row) => {
    // In ADD mode (!autofillData), just mark row as non-editable, don't save to DB yet
    if (!autofillData) {
      setRows((rows) =>
        rows.map((r) => (r.id === row.id ? { ...r, editable: false, error: "" } : r))
      );
      return;
    }
    
    // EDIT mode: save to database immediately
    if (autofillData) {
      setDetailRows((rows) =>
        rows.map((r) => (r.id === row.id ? { ...r, saving: true, error: "" } : r))
      );
      try {
        let detailRes;
        if (row.detailId) {
          // Update existing (note: id_inbois is not updatable, removed from payload)
          detailRes = await apiUpdateInboisDetail(row.detailId, {
            bil: "",
            butiran: row.butiran,
            kuantiti: row.kuantiti,
            kadar: row.kadar,
            jumlah: row.jumlah,
          });
        } else {
          // Insert new (single row) - must have valid id_inbois
          // In edit mode: use headerFields.id
          // In add mode: create draft inbois first if not exists
          let id_inbois = headerFields.id;
          if (!id_inbois) {
            // ADD mode: create draft inbois record first
            id_inbois = await createDraftInbois();
          }
          
          const payload = {
            id_inbois: id_inbois,
            butiran: row.butiran,
            kuantiti: row.kuantiti,
            kadar: row.kadar,
            jumlah: row.jumlah,
            bil: "",
          };
          detailRes = await apiTambahInboisDetailSingle(payload);
        }
        if (detailRes && (detailRes.success || detailRes.id)) {
          setDetailRows((rows) =>
            rows.map((r) =>
              r.id === row.id
                ? {
                    ...r,
                    editable: false,
                    saving: false,
                    error: "",
                    detailId: detailRes.id || r.detailId,
                  }
                : r
            )
          );
        } else {
          throw new Error("API gagal");
        }
      } catch (e) {
        const errorMsg = e.message || "Gagal menyimpan butiran. Sila cuba lagi.";
        setDetailRows((rows) =>
          rows.map((r) =>
            r.id === row.id
              ? { ...r, saving: false, error: errorMsg }
              : r
          )
        );
        // eslint-disable-next-line no-console
        console.error("Gagal simpan/update inbois_detail:", e);
        console.error("headerFields:", headerFields);
        console.error("row data:", row);
      }
    } else {
      setRows((rows) => rows.map((r) => (r.id === row.id ? { ...r, saving: true, error: "" } : r)));
      try {
        let detailRes;
        if (row.detailId) {
          // Update existing
          detailRes = await apiUpdateInboisDetail(row.detailId, {
            bil: "",
            butiran: row.butiran,
            kuantiti: row.kuantiti,
            kadar: row.kadar,
            jumlah: row.jumlah,
          });
        } else {
          // Insert new (single row)
          detailRes = await apiTambahInboisDetailSingle({
            id_inbois: 0,
            butiran: row.butiran,
            kuantiti: row.kuantiti,
            kadar: row.kadar,
            jumlah: row.jumlah,
            bil: "",
          });
        }
        if (detailRes && (detailRes.success || detailRes.id)) {
          setRows((rows) =>
            rows.map((r) =>
              r.id === row.id
                ? {
                    ...r,
                    editable: false,
                    saving: false,
                    error: "",
                    detailId: detailRes.id || r.detailId,
                  }
                : r
            )
          );
        } else {
          throw new Error("API gagal");
        }
      } catch (e) {
        setRows((rows) =>
          rows.map((r) =>
            r.id === row.id
              ? { ...r, saving: false, error: "Gagal menyimpan butiran. Sila cuba lagi." }
              : r
          )
        );
        // eslint-disable-next-line no-console
        console.error("Gagal simpan/update inbois_detail:", e);
      }
    }
  };

  // Use detailRows for edit mode, rows for add mode
  const tableRows = autofillData ? detailRows : rows;

  // Simpan handler: update inbois table in edit mode
  const handleSimpanEdit = async () => {
    setError("");
    setSaving(true);
    // Validate detailRows
    if (detailRows.length === 0) {
      showWarning("Tiada item untuk disimpan.");
      setSaving(false);
      return;
    }
    const hasEditable = detailRows.some((row) => row.editable);
    if (hasEditable) {
      showWarning(
        "Sila sahkan semua item sebelum simpan. Terdapat item yang masih belum disahkan."
      );
      setSaving(false);
      return;
    }
    // Calculate new jumlah
    const totalJumlah = detailRows.reduce((sum, row) => sum + Number(row.jumlah || 0), 0);
    try {
      // Update inbois table by id (not by no_inbois)
      await apiUpdateInbois(headerFields.id, {
        ...headerFields,
        jumlah: totalJumlah,
      });

      // Update lejar table for this inbois (by no_inbois)
      const lejarPayload = {
        // Only update fields that are relevant for lejar
        perkara: "Open Inbois (Pelbagai)",
        debit: parseFloat(totalJumlah),
        kredit: 0.0,
        id_pengguna: (user.id_pengguna || "").toLowerCase(),
        tarikh_simpan: headerFields.tarikh_inbois,
        bahagian_pemantau: user.bahagian || "",
      };
      await apiUpdateLejarByNoInbois(headerFields.no_inbois, lejarPayload);

      if (onSuccess) onSuccess();
      if (onSubmit) onSubmit();
      onClose();
    } catch (e) {
      showError("Gagal mengemaskini inbois/lejar. Sila cuba lagi.");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={dialogHeaderStyles.success}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" sx={{ color: "#fff" }}>{autofillData ? "Edit Inbois Pelbagai" : "Tambah Inbois Pelbagai"}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ ...dialogContentStyles.default, minHeight: 320 }}>
        <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2, mt: 2 }}>
          <Grid item xs={6} md={6} lg={6}>
            <TextField
              label="Tarikh Inbois"
              type="date"
              value={
                autofillData
                  ? (headerFields.tarikh_inbois ? headerFields.tarikh_inbois.slice(0, 10) : "")
                  : tarikhInbois
              }
              onChange={(e) => {
                if (autofillData)
                  setHeaderFields((f) => ({ ...f, tarikh_inbois: e.target.value }));
                else setTarikhInbois(e.target.value);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2, mt: 1, minWidth: 180 }}
            />
          </Grid>
          <Grid
            item
            xs={6}
            md={6}
            lg={6}
            sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}
          >
            <MDTypography variant="caption" fontWeight="bold" sx={{ mr: 1 }}>
              ID Inbois:
            </MDTypography>
            <MDTypography
              variant="overline"
              fontWeight="bold"
              color={autofillData ? "success" : draftInboisId ? "success" : "warning"}
            >
              {autofillData ? headerFields.id : draftInboisId || "(akan dijana)"}
            </MDTypography>
            <MDTypography variant="caption" fontWeight="bold" sx={{ ml: 2, mr: 1 }}>
              No Inbois:
            </MDTypography>
            <MDTypography
              variant="overline"
              fontWeight="bold"
              color="info"
            >
              {autofillData ? headerFields.no_inbois : newNoInbois || "-"}
            </MDTypography>
          </Grid>
        </Grid>
        <TableContainer component={Paper} sx={{ boxShadow: "none", border: "1px solid #eee" }}>
          <Table size="small">
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell align="center">
                <MDTypography variant="caption" fontWeight="bold">
                  Bil.
                </MDTypography>
              </TableCell>
              <TableCell align="center" sx={{ width: "50%" }}>
                <MDTypography variant="caption" fontWeight="bold">
                  Butiran
                </MDTypography>
              </TableCell>
              <TableCell align="center" sx={{ width: "10%" }}>
                <MDTypography variant="caption" fontWeight="bold">
                  Kuantiti
                </MDTypography>
              </TableCell>
              <TableCell align="center" sx={{ width: "20%" }}>
                <MDTypography variant="caption" fontWeight="bold">
                  Kadar
                </MDTypography>
              </TableCell>
              <TableCell align="center" sx={{ width: "20%" }}>
                <MDTypography variant="caption" fontWeight="bold">
                  Jumlah
                </MDTypography>
              </TableCell>
              <TableCell align="center" sx={{ width: "20%" }}>
                <MDTypography variant="caption" fontWeight="bold">
                  {/* Ubahsuai */}
                </MDTypography>
              </TableCell>
              <TableCell align="center" sx={{ width: "20%" }}>
                <MDTypography variant="caption" fontWeight="bold">
                  {/* Hapus */}
                </MDTypography>
              </TableCell>
            </TableRow>
            <TableBody>
              {tableRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ color: "#888" }}>
                    Tiada item.
                  </TableCell>
                </TableRow>
              ) : (
                tableRows.map((row, idx) => (
                  <TableRow key={row.id} hover>
                    <TableCell align="center">
                      <MDTypography variant="caption" fontWeight="regular">
                        {idx + 1}
                      </MDTypography>
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        fullWidth
                        value={row.butiran}
                        onChange={(e) => {
                          const newRows = [...tableRows];
                          newRows[idx].butiran = e.target.value;
                          autofillData ? setDetailRows(newRows) : setRows(newRows);
                        }}
                        size="small"
                        placeholder="Butiran"
                        disabled={!row.editable}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        type="number"
                        size="small"
                        value={row.kuantiti}
                        onChange={(e) => {
                          const newRows = [...tableRows];
                          newRows[idx].kuantiti = Number(e.target.value);
                          newRows[idx].jumlah =
                            Number(newRows[idx].kuantiti) * Number(newRows[idx].kadar);
                          autofillData ? setDetailRows(newRows) : setRows(newRows);
                        }}
                        inputProps={{
                          min: 1,
                          style: { textAlign: "center" },
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                        }}
                        sx={{
                          "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                            {
                              WebkitAppearance: "none",
                              margin: 0,
                            },
                          "& input[type=number]": {
                            MozAppearance: "textfield",
                          },
                        }}
                        disabled={!row.editable}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        type="number"
                        size="small"
                        value={row.kadar}
                        onChange={(e) => {
                          const newRows = [...tableRows];
                          newRows[idx].kadar = Number(e.target.value);
                          newRows[idx].jumlah =
                            Number(newRows[idx].kuantiti) * Number(newRows[idx].kadar);
                          autofillData ? setDetailRows(newRows) : setRows(newRows);
                        }}
                        inputProps={{ min: 0, step: 0.01, style: { textAlign: "center" } }}
                        placeholder="Kadar"
                        sx={{
                          "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                            {
                              WebkitAppearance: "none",
                              margin: 0,
                            },
                          "& input[type=number]": {
                            MozAppearance: "textfield",
                          },
                        }}
                        disabled={!row.editable}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <MDTypography fontSize={15}>
                        {parseFloat(row.jumlah || 0).toFixed(2)}
                      </MDTypography>
                    </TableCell>
                    <TableCell align="center">
                      {row.editable ? (
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleSaveRow(row)}
                          disabled={row.saving}
                        >
                          {row.saving ? (
                            <span
                              className="MuiCircularProgress-root MuiCircularProgress-colorPrimary"
                              style={{
                                width: 18,
                                height: 18,
                                display: "inline-block",
                                verticalAlign: "middle",
                              }}
                            />
                          ) : (
                            <CheckIcon fontSize="small" />
                          )}
                        </IconButton>
                      ) : (
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => handleEditItem(row.id)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                      {row.error && (
                        <MDTypography variant="caption" color="error">
                          {row.error}
                        </MDTypography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteItem(row.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions sx={dialogContentStyles.default}>
        <Grid container alignItems="center">
          <Grid
            item
            xs={12}
            md={6}
            lg={6}
            sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}
          >
            <Button
              variant="contained"
              size="small"
              sx={{ ...buttonStyles.save, minWidth: 110 }}
              onClick={handleAddItem}
            >
              Item Baru
            </Button>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            lg={6}
            sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
          >
            <Button
              onClick={autofillData ? handleSimpanEdit : handleSimpan}
              variant="contained"
              size="small"
              sx={buttonStyles.save}
              disabled={saving}
            >
              {saving ? "Menyimpan..." : "Simpan"}
            </Button>
            <Button
              onClick={onClose}
              variant="contained"
              size="small"
              sx={buttonStyles.cancel}
            >
              BATAL
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
}

TambahInboisPelbagai.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  onSuccess: PropTypes.func,
  selectedAkaun: PropTypes.object,
  autofillData: PropTypes.object,
};

export default TambahInboisPelbagai;
